import React from 'react';
import { bool, string } from 'prop-types';
import classNames from 'classnames';

import { intlShape } from '../../util/reactIntl';
import {
  autocompletePlaceSelected,
  autocompleteSearchRequired,
  composeValidators,
} from '../../util/validators';

import { FieldLocationAutocompleteInput } from '../../components';

const identity = v => v;

/**
 * Ort ("home location") input built on the same Mapbox autocomplete the
 * listing location step uses. The selected place is serialized to
 * protectedData.homeLocation by the submit handlers (see
 * util/homeLocation.js) — it never goes through the generic
 * userFields/getNonUserFieldParams path, since the raw form value contains
 * SDK types and prediction objects that must not be stored as-is.
 *
 * Used in SignupForm, ConfirmSignupForm and ProfileSettingsForm, so the
 * translation keys live under a shared UserFieldHomeLocation namespace.
 */
const UserFieldHomeLocation = props => {
  const { rootClassName, className, formId, intl, values, isRequired } = props;

  const requiredValidatorsMaybe = isRequired
    ? {
        validate: composeValidators(
          autocompleteSearchRequired(
            intl.formatMessage({ id: 'UserFieldHomeLocation.required' })
          ),
          autocompletePlaceSelected(
            intl.formatMessage({ id: 'UserFieldHomeLocation.notRecognized' })
          )
        ),
      }
    : {
        // Optional: only complain if the user typed something without
        // picking a suggestion — an empty field is fine.
        validate: value =>
          value?.search
            ? autocompletePlaceSelected(
                intl.formatMessage({ id: 'UserFieldHomeLocation.notRecognized' })
              )(value)
            : undefined,
      };

  return (
    <FieldLocationAutocompleteInput
      rootClassName={classNames(className, { [rootClassName]: !!rootClassName })}
      name="homeLocation"
      label={intl.formatMessage({ id: 'UserFieldHomeLocation.label' })}
      placeholder={intl.formatMessage({ id: 'UserFieldHomeLocation.placeholder' })}
      useDefaultPredictions={false}
      format={identity}
      valueFromForm={values?.homeLocation}
      {...requiredValidatorsMaybe}
    />
  );
};

UserFieldHomeLocation.defaultProps = {
  rootClassName: null,
  className: null,
  formId: null,
  isRequired: true,
};

UserFieldHomeLocation.propTypes = {
  rootClassName: string,
  className: string,
  formId: string,
  isRequired: bool,
  intl: intlShape.isRequired,
};

export default UserFieldHomeLocation;
